import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import { addOneEventToStore, updateEventInStore } from 'store/actions/event';
import { createEventValidationConfig } from 'app/shared/utils/form/validation/event';

import styles from '../event.module.css';
import { DetailItem } from 'app/shared/info-section/item';
import useValidator from 'hooks/validator';
import { useEventsService } from 'hooks/automation/event';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { PermissionsContext } from 'contexts/permissions';

/**
 * Create/update form for custom triggers. Rendered inside a modal: onSave fires
 * after a successful save, onCancel closes without saving.
 */
export const EventForm = ({ data = {}, onSave = () => {}, onCancel = () => {} }) => {
  const dispatch = useDispatch();
  const { permissions } = useContext(PermissionsContext);

  const [loading, setLoading] = useState(false);
  const [form_data, setFormData] = useState({
    name: '',
    code: '',
    resource_class: '',
    in_use: false
  });
  const { errors, validateField, form_is_valid } = useValidator(createEventValidationConfig);
  const { createEvent, updateEvent } = useEventsService();

  useEffect(() => {
    if (!Object.keys(data).length) return;
    setFormData(() => data);
  }, [data]);

  const clearForm = () => {
    setFormData({});
  };

  const submitForm = async () => {
    try {
      setLoading(true);
      const data = {
        ...form_data,
        resource_class: 'custom'
      };

      if (!form_data.id) {
        const { event } = await createEvent({ data });
        if (!event) return;

        toast.success('Trigger created successfully.');
        dispatch(addOneEventToStore({ ...event, code: form_data.code }));
        clearForm();
        onSave(event);
      } else {
        const { event } = await updateEvent(form_data.id, { data });
        if (!event) return;
        toast.success('Trigger updated successfully.');

        const values = { ...data };
        dispatch(updateEventInStore(values));
        clearForm();
        onSave(values);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event;
    const code_in_use = form_data.id && data.in_use;

    let parsed_value = '';
    if (name === 'name') {
      parsed_value = value.replace(/ /g, '_');
      setFormData((prevData) => ({
        ...prevData,
        name: value,
        code: code_in_use || form_data.id ? prevData.code : parsed_value
      }));
      validateField(name, value);
      validateField('code', parsed_value);
    } else {
      parsed_value = value;
      if (name === 'code' && code_in_use) return;
      if (name === 'code') {
        parsed_value = value.replace(/ /g, '_');
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: parsed_value
      }));

      validateField(name, parsed_value);
    }
  };

  return (
    <div className={styles.eventForm}>
      <GridRow num_of_columns={1} style={{ gap: '0' }}>
        <GridColumn>
          <DetailItem title="Name">
            <SimpleInput
              input_id="name"
              name="name"
              value={form_data.name}
              onInput={handleChange}
              error={errors.name}
              required
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Code">
            <SimpleInput
              input_id="code"
              name="code"
              value={form_data.code}
              onInput={handleChange}
              error={errors.code}
              required
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <div className={cx('grid mt-2', styles.action)}>
            <Button type="secondary" onClick={onCancel} text="Cancel" />
            <Button
              onClick={submitForm}
              text="Save"
              className="float-right"
              loading={loading}
              disabled={
                !form_is_valid || loading || !form_data.id
                  ? !permissions['automation_event:create']
                  : !permissions['automation_event:update']
              }
            />
          </div>
        </GridColumn>
      </GridRow>
    </div>
  );
};
